import { Draggable, getDroppablePayload } from "@msuite/ui/dnd";
import type { Row, schema, ZeroVehicleAssignmentPlan } from "@msuite/zero";
import { UserCard } from "@/components/cards";
import {
	type UserAssignmentType,
	type UserType,
	useCrewHandler,
} from "@/sites/plan/plan-kanban/use-crew-handler";
import { useDailyContext } from "../daily/context";
import { useCrewContext } from "./context";

/** Props Interface */
interface CrewStaffAssignmentProps {
	assignment: Row<typeof schema.tables.userAssignments>;
}

export function CrewStaffAssignment({ assignment }: CrewStaffAssignmentProps) {
	/** Context */
	const { crew, isStaffingBlocked } = useCrewContext();
	const { date } = useDailyContext();
	const crewHandler = useCrewHandler({ crewId: crew.id, planId: assignment.planId });

	/** Functions */
	return (
		<Draggable
			type="user-assignment"
			className="not-first:pt-1 not-last:pb-1"
			accept={["user", "user-assignment", "user-vehicle-assignment"]}
			key={assignment.id}
			item={assignment}
			allowedEdges={["top", "bottom"]}
			handleOnDrop={(element) => {
				const payload = getDroppablePayload(element);
				const edge = crewHandler.getClosestEdge(element);
				if (payload.type === "user") {
					crewHandler.onDropUser((payload.item as UserType).id, {
						edge,
						assignmentId: assignment.id,
					});
				} else if (payload.type === "user-assignment") {
					crewHandler.onDropUser((payload.item as UserAssignmentType).userId, {
						edge,
						assignmentId: assignment.id,
					});
				} else if (payload.type === "user-vehicle-assignment") {
					const userId = (payload.item as ZeroVehicleAssignmentPlan).userId;
					if (!userId) return;
					crewHandler.onDropUser(userId, {
						edge,
						assignmentId: assignment.id,
					});
				}
			}}
			isDisabled={isStaffingBlocked}
		>
			<UserCard
				userId={assignment.userId}
				absenceTargetDate={date}
				withPopover={false}
			/>
		</Draggable>
	);
}
